/**
 * Public read-only share page (doc/12 §3.2.1) — /s/<share_id>.
 *
 * Renders without auth: either a snapshotted document (markdown) or a
 * live card graph (grouped card list — deliberately lighter than the
 * in-app ReactFlow graph; this page is most often opened inside WeChat
 * on a phone). The footer is the propagation hook: product mark + CTA.
 *
 * Not-found and revoked are the same state by design (the API returns
 * 404 for both) — no oracle for "this link existed once".
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiFetch } from '../api';

type ShareData =
  | {
      kind: 'document';
      title: string;
      markdown: string;
      created_at: string;
    }
  | {
      kind: 'graph';
      title: string;
      cards: { id: string; kind: string; title: string; body: string; stale: boolean }[];
      edges: { source: string; target: string; type: string }[];
      created_at: string;
    };

type LoadState =
  | { st: 'loading' }
  | { st: 'gone' }
  | { st: 'ok'; data: ShareData };

/** Card kinds in display order — decisions first (they're the story),
 *  notes last. Mirrors the epistemic-kind palette used in-app. */
const KIND_ORDER = ['decision', 'statement', 'question', 'observation', 'dead_end', 'pivot', 'note'];
const KIND_TINT: Record<string, string> = {
  decision: '#7c5cbf',
  statement: '#3a6ea5',
  question: '#b8860b',
  observation: '#2e7d57',
  dead_end: '#8a8a8a',
  pivot: '#c06030',
  note: '#6b6b6b',
};

export function SharePage({ shareId }: { shareId: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ st: 'loading' });

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/shares/${encodeURIComponent(shareId)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json() as Promise<ShareData>;
      })
      .then((data) => {
        if (!cancelled) setState({ st: 'ok', data });
      })
      .catch(() => {
        if (!cancelled) setState({ st: 'gone' });
      });
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper-0)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <main
        style={{
          flex: 1,
          width: '100%',
          maxWidth: 720,
          margin: '0 auto',
          padding: '32px 20px 48px',
          boxSizing: 'border-box',
        }}
      >
        {state.st === 'loading' && (
          <div style={{ color: 'var(--ink-3)', fontSize: 14, textAlign: 'center', paddingTop: 80 }}>
            …
          </div>
        )}
        {state.st === 'gone' && (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div className="serif" style={{ fontSize: 20, color: 'var(--ink-0)', marginBottom: 8 }}>
              {t('share.goneTitle')}
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>{t('share.goneBody')}</div>
          </div>
        )}
        {state.st === 'ok' && state.data.kind === 'document' && (
          <DocumentView data={state.data} />
        )}
        {state.st === 'ok' && state.data.kind === 'graph' && <GraphView data={state.data} />}
      </main>
      <Footer />
    </div>
  );
}

function DocumentView({ data }: { data: Extract<ShareData, { kind: 'document' }> }) {
  return (
    <article>
      <h1
        className="serif"
        style={{ fontSize: 28, color: 'var(--ink-0)', lineHeight: 1.25, marginBottom: 6 }}
      >
        {data.title}
      </h1>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 28 }}>
        {new Date(data.created_at).toLocaleDateString()}
      </div>
      <div className="share-markdown" style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.markdown}</ReactMarkdown>
      </div>
    </article>
  );
}

function GraphView({ data }: { data: Extract<ShareData, { kind: 'graph' }> }) {
  const { t } = useTranslation();
  const byKind = new Map<string, typeof data.cards>();
  for (const c of data.cards) {
    const k = byKind.get(c.kind) ?? [];
    k.push(c);
    byKind.set(c.kind, k);
  }
  const kinds = [
    ...KIND_ORDER.filter((k) => byKind.has(k)),
    ...[...byKind.keys()].filter((k) => !KIND_ORDER.includes(k)),
  ];
  return (
    <div>
      <h1
        className="serif"
        style={{ fontSize: 26, color: 'var(--ink-0)', lineHeight: 1.25, marginBottom: 6 }}
      >
        {data.title}
      </h1>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 24 }}>
        {t('share.graphStats', { cards: data.cards.length, edges: data.edges.length })}
      </div>
      {kinds.map((kind) => (
        <section key={kind} style={{ marginBottom: 26 }}>
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: KIND_TINT[kind] ?? 'var(--ink-3)',
              marginBottom: 10,
            }}
          >
            {kind} · {byKind.get(kind)!.length}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {byKind.get(kind)!.map((c) => (
              <div
                key={c.id}
                style={{
                  background: 'white',
                  border: '1px solid var(--rule)',
                  borderLeft: `3px solid ${KIND_TINT[kind] ?? 'var(--rule)'}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  opacity: c.stale ? 0.65 : 1,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ink-0)',
                    marginBottom: c.body ? 5 : 0,
                    textDecoration: c.stale ? 'line-through' : 'none',
                  }}
                >
                  {c.title}
                  {c.stale && (
                    <span
                      className="mono"
                      style={{ fontSize: 9.5, color: '#b8860b', marginLeft: 8, textDecoration: 'none' }}
                    >
                      STALE
                    </span>
                  )}
                </div>
                {c.body && (
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: 'var(--ink-2)',
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {c.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer
      style={{
        borderTop: '1px solid var(--rule)',
        background: 'white',
        padding: '20px 20px calc(20px + env(safe-area-inset-bottom))',
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: 'var(--ink-0)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            fontSize: 14,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          D
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-0)' }}>Draftlize</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{t('share.footerTagline')}</div>
        </div>
        <a
          href="/"
          style={{
            flexShrink: 0,
            padding: '8px 14px',
            background: 'var(--ink-0)',
            color: 'white',
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          {t('share.footerCta')}
        </a>
      </div>
    </footer>
  );
}
